import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { Claim, Sku, Product } from "../api/models"
import { addEntityFetchReducers, EntityState } from "./entity"
import { logOut } from "./globalSlice"
import { RootState } from "./store"
import Api from "../api/api"
import { activePlansThunk } from "./plansSlice"
import { subscriptionThunk, switchSubscriptionThunk } from "./subscriptionSlice"
import { useFetchableState } from "./fetchableState"
import { redeemCodeThunk, redeemResellerCodeThunk, redemptionsThunk, redemptionThunk } from "./redemptionsSlice"
import { storeSkusThunk, storeSkuThunk } from "./storeSlice"
import { productFetchThunk } from "./productsSlice"
import { licenseCollectionThunk, licenseThunk } from "./licensesSlice"

interface SkusState extends EntityState<Sku> {
    debug: string
}

const initialState: SkusState = {
    entities: {},
    debug: "skus state",
}

export const skusSlice = createSlice({
    name: 'skus',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            Sku,
            { sku: Sku, claims: Claim[], products: Product[] },
            { id: string }
        >(
            builder,
            (payload) => payload.sku,
            skuThunk
        )

        builder.addCase(activePlansThunk.fulfilled, (state, action) => {
            const { skus } = action.payload
            skus.forEach((sku: Sku) => {
                state.entities[sku.id] = {
                    data: sku,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(subscriptionThunk.fulfilled, (state, action) => {
            const { sku } = action.payload
            if (sku) {
                state.entities[sku.id] = {
                    data: sku,
                    lastFetched: Date.now()
                }
            }
        })

        builder.addCase(switchSubscriptionThunk.fulfilled, (state, action) => {
            const { sku } = action.payload
            state.entities[sku.id] = {
                data: sku,
                lastFetched: Date.now()
            }
        })

        builder.addCase(redemptionThunk.fulfilled, (state, action) => {
            const { sku } = action.payload
            state.entities[sku.id] = {
                data: sku,
                lastFetched: Date.now()
            }
        })

        builder.addCase(redemptionsThunk.fulfilled, (state, action) => {
            const { skus } = action.payload
            skus.forEach(sku => {
                state.entities[sku.id] = {
                    data: sku,
                    lastFetched: Date.now()
                }
            })
        })

        // a code redemption and a reseller-code redemption side-load the same entities
        const onCodeRedeemed = (state: SkusState, action: { payload: { skus: Sku[] } }) => {
            const { skus } = action.payload
            skus.forEach(sku => {
                state.entities[sku.id] = {
                    data: sku,
                    lastFetched: Date.now()
                }
            })
        }
        builder.addCase(redeemCodeThunk.fulfilled, onCodeRedeemed)
        builder.addCase(redeemResellerCodeThunk.fulfilled, onCodeRedeemed)

        builder.addCase(storeSkuThunk.fulfilled, (state, action) => {
            const { sku } = action.payload
            state.entities[sku.id] = {
                data: sku,
                lastFetched: Date.now()
            }
        })

        builder.addCase(storeSkusThunk.fulfilled, (state, action) => {
            const { skus } = action.payload
            skus.forEach((sku: Sku) => {
                state.entities[sku.id] = {
                    data: sku,
                    lastFetched: Date.now()
                }
            })
        })


        builder.addCase(productFetchThunk.fulfilled, (state, action) => {
            const { skus } = action.payload
            skus.forEach((sku: Sku) => {
                state.entities[sku.id] = {
                    data: sku,
                    lastFetched: Date.now()
                }
            })
        })

        builder.addCase(licenseCollectionThunk.fulfilled, (state, action) => {
            const { skus } = action.payload
            skus.forEach((sku: Sku) => {
                state.entities[sku.id] = {
                    data: sku,
                    lastFetched: Date.now()
                }
            })
        })

        builder.addCase(licenseThunk.fulfilled, (state, action) => {
            const { skus } = action.payload
            skus.forEach((sku: Sku) => {
                state.entities[sku.id] = {
                    data: sku,
                    lastFetched: Date.now()
                }
            })
        })

        builder.addCase(logOut, (state: SkusState) => {
            state = initialState;
        })
    },
})

// GET skus/:skuId

export const skuThunk = createAsyncThunk(
    'skus/fetchSku',
    async ({ id }: { id: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.skus.entities[id]?.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getSku({ skuId: id })
    }
)

// export const skuEntity = (id: string) => {
//     return {
//         selector: (state: RootState) => state.skus.entities[id],
//         action: skuThunk({ id })
//     }
// }

export function useSku(id: string/*, accessToken: string*/) {
    return useFetchableState<Sku>({
        selector: (state: RootState) => state.skus.entities[id],
        action: skuThunk({ id })
    })
}


export default skusSlice.reducer